/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import user from 'js/lib/user';

import { Grid, Link, Tag, Typography2, breakpoints } from '@coursera/cds-core';

import EnterpriseSkillPathwayEnrollButtonNotConfigurable from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayEnrollButtonNotConfigurable';
import ToolsSection from 'bundles/enterprise-learning-path-description-page/components/product-overview/ToolsSection';
import SkillPathwayCareerBandKeyDetailsItem from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCareerBandKeyDetailsItem';
import { useSkillPathway } from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayContext';
import LearningObjectives from 'bundles/enterprise-learning-path-description-page/modules/LearningObjectives';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageQuery';
import type { QueryVerifiedSkillPathsPageQuery } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/GetSkillPathwayQueryV2';
import { SKILL_PATHWAY_NAV_ITEMS } from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import { getInstructorsFromVspPageData } from 'bundles/enterprise-learning-path-description-page/utils/vspXdpV2Utils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import type { InstructorFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';

import DataHero from 'images/enterprise-learning-path-description-page/data-hero.png';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

export type VspPageV2Data = NonNullable<
  NonNullable<QueryVerifiedSkillPathsPageQuery['DescriptionPage']>['queryVerifiedSkillPathsPage']
>;

const styles = {
  link: css`
    /* override cds link color */
    color: var(--cds-color-neutral-primary) !important;
  `,
  flushBackgroundImage: css`
    position: absolute;
    right: var(--cds-spacing-200);
    width: auto;
    z-index: -1;
    ${breakpoints.up('md')} {
      right: max(var(--cds-spacing-600), calc((100vw - ${breakpoints.values.lg}px) / 2 + var(--cds-spacing-600)));
    }
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment;
  vspPageV2Data?: VspPageV2Data;
};

const EnterpriseSkillPathwayProductOverviewModuleRenderer = ({ pageData, vspPageV2Data }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const thirdPartyOrg = useThirdPartyOrganization();
  const skillPathway = useSkillPathway();
  const instructors = getInstructorsFromVspPageData(vspPageV2Data);

  const useV2Variant = user.isAuthenticatedUser();

  const keyInformationItems = [
    <KeyInformationDetailsItem
      title={
        <TrackedDiv trackingName="kim_view_syllabus">
          <Link
            href={`#${useV2Variant ? SKILL_PATHWAY_NAV_ITEMS.SKILL_GOALS : SPECIALIZATION_NAV_ITEMS.COURSES}`}
            css={styles.link}
          >
            {_t('#{courseCount} skill goals', {
              courseCount: skillPathway?.skillAreasWithUserData?.length || 0,
            })}
          </Link>
        </TrackedDiv>
      }
      subtitle={_t('Covers critical skills required at this career level ')}
      key="skill-goals"
    />,
    <SkillPathwayCareerBandKeyDetailsItem careerBand={skillPathway?.careerBand} key="career-band" />,
    <KeyInformationDetailsItem
      title={_t('Track your mastery')}
      subtitle={_t('Learn with comprehensive job-aligned assessments.')}
      key="skill-mastery"
    />,
    useV2Variant ? (
      <KeyInformationDetailsItem
        title={_t('2 months at 9 hours a week')}
        subtitle={_t('Flexible schedule')}
        key="flexible-schedule"
      />
    ) : (
      <KeyInformationDetailsItem
        title={_t('Flexible schedule')}
        subtitle={
          <React.Fragment>
            <div>{_t('2 months at 9 hours a week')}</div>
            <div>{_t('Learn at your own pace')}</div>
          </React.Fragment>
        }
        key="flexible-schedule"
      />
    ),
  ];

  const smSpacing = useV2Variant ? 32 : 48;

  return (
    <React.Fragment>
      <ProductOverviewHorizontalModule
        disablePageNavigationDisplay={true}
        navigationLinks={navigationLinks}
        anchorId={SKILL_PATHWAY_NAV_ITEMS.ABOUT}
        heroBackgroundColor={HERO_BACKGROUND_COLOR}
        overviewBackgroundImage={
          <Imgix
            src={DataHero}
            alt={_t('Skill Pathway Background Illustration')}
            width="100%"
            height="376px"
            css={styles.flushBackgroundImage}
          />
        }
        hero={
          <HeroModule
            productTitle={pageData?.enterpriseBadge?.name}
            productTag={
              <Tag color="highlight1" priority="primary" variant="status">
                {_t('Early Access')}
              </Tag>
            }
            description={
              <Typography2 component="p" variant="bodyPrimary">
                {pageData?.enterpriseBadge?.description}
              </Typography2>
            }
            primaryCta={<EnterpriseSkillPathwayEnrollButtonNotConfigurable pageData={pageData} />}
            additionalInfo={
              thirdPartyOrg?.name &&
              !useV2Variant && (
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {_t('Access provided by #{orgName}', {
                    orgName: thirdPartyOrg?.name,
                  })}
                </Typography2>
              )
            }
            instructors={
              useV2Variant ? <HeroInstructors instructors={instructors as Array<InstructorFragment>} /> : undefined
            }
            hideBackgroundImage
          />
        }
        keyInformation={
          <KeyInformationHorizontalModule keyInformationItems={keyInformationItems} disableMobileDivider />
        }
        aboutSubmodule={
          <Grid container item spacing={{ xs: 0, sm: smSpacing }} alignItems="flex-start">
            <Grid item xs={12} sm={8}>
              <LearningObjectives
                learningObjectives={pageData?.enterpriseBadge?.learningObjectives}
                moduleSpacing={32}
                checkSize="large"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ToolsSection
                tools={
                  pageData?.enterpriseBadge?.skillsMetadata?.map((skill) => skill?.skillName || null).filter(Boolean) ||
                  []
                }
              />
            </Grid>
            {/* {enableVspXdpV2 && (
              <Grid item xs={12}>
                <IncludedSection
                  totalAssignments={totalAssignments}
                  totalQuizzes={totalQuizzes}
                  languageInfo={languageInfo}
                />
              </Grid>
            )} */}
          </Grid>
        }
      />
    </React.Fragment>
  );
};

export default EnterpriseSkillPathwayProductOverviewModuleRenderer;
